import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomerNavigation from '../../components/CustomerNavigation';
import KakaoChatButton from '../../components/KakaoChatButton';
import TermsHeader from './components/TermsHeader';
import TermsActions from './components/TermsActions';
import { supabase } from '../../lib/supabaseClient';
import { useServiceRequest } from '../../components/ServiceStatusProvider';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const { requestCleaning } = useServiceRequest();
  const location = useLocation();
  const [agreed, setAgreed] = useState(false);
  const showAcceptButton = location?.state?.fromBooking || false;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTerm, setActiveTerm] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const { data, error } = await supabase
          .from('terms')
          .select('id, title, content, version, is_active, created_at')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw error;
        const row = data?.[0];
        if (!row) throw new Error('약관 정보를 불러올 수 없습니다.');

        if (!mounted) return;
        setActiveTerm(row);
      } catch (e) {
        if (!mounted) return;
        setActiveTerm(null);
        setErrorMessage(e?.message || '약관 정보를 불러올 수 없습니다.');
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, []);

  const termsHeaderData = useMemo(() => {
    const createdAt = activeTerm?.created_at ? new Date(activeTerm.created_at) : null;
    const createdAtStr = createdAt && !Number.isNaN(createdAt.getTime())
      ? createdAt.toLocaleDateString('ko-KR')
      : '-';

    return {
      effectiveDate: createdAtStr,
      version: activeTerm?.version || '-',
      lastUpdated: createdAtStr,
    };
  }, [activeTerm]);


  const handleAccept = () => {
    if (agreed) {
      if (showAcceptButton) {
        requestCleaning({ state: { termsAgreed: true } });
      } else {
        alert('약관에 동의하셨습니다.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CustomerNavigation />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-lg bg-card p-6 shadow-lg transition-all duration-300 hover:shadow-xl sm:p-8">
          <TermsHeader
            effectiveDate={termsHeaderData?.effectiveDate}
            version={termsHeaderData?.version}
            lastUpdated={termsHeaderData?.lastUpdated}
          />

          {errorMessage && (
            <div className="mb-6 rounded-lg bg-error/10 p-4">
              <p className="text-sm font-medium text-error">{errorMessage}</p>
            </div>
          )}

          {isLoading ? (
            <div className="mb-8 rounded-lg bg-muted p-6 text-sm text-text-secondary">
              약관 정보를 불러오는 중...
            </div>
          ) : (
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold text-foreground">
                {activeTerm?.title || '약관'}
              </h2>
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-text-secondary">
                {activeTerm?.content || ''}
              </div>
            </div>
          )}

          <div className="mt-8">
            <TermsActions
              agreed={agreed}
              onAgreeChange={setAgreed}
              onAccept={handleAccept}
              showAcceptButton={showAcceptButton}
            />
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-text-secondary">
            &copy; {new Date()?.getFullYear()} 스니커케어프로. All rights reserved.
          </p>
        </div>
      </main>

      <KakaoChatButton />
    </div>
  );
};

export default TermsAndConditions;